import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, money } from "@/lib/money";
import { PortalShell, PortalMessage } from "@/components/portal/PortalShell";

const PayButtons = ({ token, d }) => {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const pay = async (kind) => {
    setBusy(kind);
    setError("");
    try {
      const { data } = await api.post(`/portal/invoices/${token}/checkout`, { kind, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { setError(formatApiError(err)); setBusy(""); }
  };
  const btn = "flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-medium transition-[opacity] disabled:opacity-50";
  return (
    <div className="mt-8 space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row">
        {d.deposit_due > 0 && (
          <button onClick={() => pay("deposit")} disabled={!!busy} className={`${btn} bg-rc-copper text-white`} data-testid="pay-deposit-btn">
            {busy === "deposit" && <Loader2 className="h-4 w-4 animate-spin" />} Pay deposit {money(d.deposit_due)}
          </button>
        )}
        <button onClick={() => pay(d.amount_paid > 0 ? "balance" : "full")} disabled={!!busy} className={`${btn} ${d.deposit_due > 0 ? "border border-rc-line2 text-rc-ink" : "bg-rc-copper text-white"}`} data-testid="pay-balance-btn">
          {(busy === "balance" || busy === "full") && <Loader2 className="h-4 w-4 animate-spin" />} {d.amount_paid > 0 ? "Pay balance" : "Pay in full"} {money(d.balance_due)}
        </button>
      </div>
      {error && <p className="text-sm text-red-600" data-testid="pay-error">{error}</p>}
      <p className="text-xs text-rc-muted">Secure card payment by Stripe.</p>
    </div>
  );
};

const Row = ({ label, value, strong, testId }) => (
  <div className={`flex justify-between py-2 ${strong ? "font-display text-2xl text-rc-ink" : "text-sm text-rc-body"}`} data-testid={testId}>
    <span>{label}</span><span>{value}</span>
  </div>
);

export default function InvoicePortal() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    api.get(`/portal/invoices/${token}`).then((r) => setState({ data: r.data })).catch((e) => setState({ error: formatApiError(e) }));
  }, [token]);

  const d = state.data;
  return (
    <PortalShell testId="invoice-portal">
      {state.loading && <Loader2 className="mx-auto h-6 w-6 animate-spin text-rc-copper" />}
      {state.error && <PortalMessage title="Link unavailable" text={state.error} />}
      {d && (
        <>
          <p className="font-mono-rc text-xs uppercase tracking-[0.25em] text-rc-copper">Invoice {d.number}</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl" data-testid="invoice-title">{d.title}</h1>
          <p className="mt-2 text-rc-body">For {d.client_name}{d.due_date ? ` · Due ${fmtDate(d.due_date + "T12:00:00")}` : ""}</p>
          <div className="mt-8 rounded-[24px] border border-rc-line bg-rc-surface p-6 sm:p-8">
            <div className="divide-y divide-rc-line">
              {(d.line_items || []).map((li, i) => (
                <div key={i} className="flex justify-between gap-4 py-3 text-sm">
                  <span className="text-rc-ink">{li.description}<span className="block text-xs text-rc-muted">{li.quantity} × {money(li.unit_price)}</span></span>
                  <span className="text-rc-ink">{money(li.quantity * li.unit_price)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 border-t border-rc-line pt-3">
              <Row label="Total" value={money(d.total)} strong testId="invoice-portal-total" />
              {d.deposit_amount > 0 && <Row label={`Deposit${d.deposit_type === "percent" ? ` (${d.deposit_value}%)` : ""}`} value={money(d.deposit_amount)} />}
              {d.amount_paid > 0 && <Row label="Paid so far" value={money(d.amount_paid)} testId="invoice-portal-paid" />}
              <Row label="Balance due" value={money(d.balance_due)} testId="invoice-portal-balance" />
            </div>
            {d.notes && <p className="mt-4 whitespace-pre-wrap rounded-xl bg-rc-bg p-4 text-sm text-rc-body">{d.notes}</p>}
          </div>
          {d.status === "paid" ? (
            <p className="mt-8 flex items-center gap-2 rounded-2xl bg-emerald-50 p-5 font-medium text-emerald-800" data-testid="invoice-paid"><CheckCircle2 className="h-5 w-5" /> Paid in full — thank you!</p>
          ) : <PayButtons token={token} d={d} />}
          {d.amount_paid > 0 && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/invoices/${token}/pdf`} className="mt-6 inline-flex items-center gap-2 rounded-full border border-rc-line2 px-6 py-3 text-sm font-medium text-rc-ink hover:border-rc-copper" data-testid="invoice-pdf-download">
              <Download className="h-4 w-4" /> Download {d.status === "paid" ? "paid invoice" : "receipt"} (PDF)
            </a>
          )}
          {d.payments?.length > 0 && (
            <div className="mt-8 text-sm text-rc-body">
              <p className="font-mono-rc text-[11px] uppercase tracking-[0.2em] text-rc-muted">Payments</p>
              {d.payments.map((p, i) => <p key={i} className="mt-1">{money(p.amount)} · {p.kind} · {fmtDate(p.paid_at)}</p>)}
            </div>
          )}
        </>
      )}
    </PortalShell>
  );
}
